import {
  CORE_VERSION,
  SCHEMA_VERSION,
  toIso8601,
  wallClockOf,
  DEFAULT_PREFERENCES,
  readPreferences,
  writePreferences,
  type Preferences,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'

import {
  THEME_CHOICES,
  readTheme,
  writeTheme,
  type ThemeChoice,
} from '../appearance/theme.js'
import { lastBackupShort } from '../backup/describe.js'
import { useBackupHealth } from '../backup/useBackupHealth.js'
import { Page, Row, RowLink, Section, Segmented, Tile } from '../components/Layout.js'
import { Toggle } from '../components/Toggle.js'
import { describePersistence } from '../pwa/persistence.js'
import { useStorageStatus } from '../pwa/useStorageStatus.js'
import { useServices } from '../services/ServicesProvider.js'
import styles from './SettingsScreen.module.css'

/** Icon paths from the Meadow design, on its 24-unit grid. */
const ICON = {
  tracks: 'M4 19V5m0 14h16M8 15l4-5 3 3 4-6',
  checkins: 'M5 5h14v14H5zM8 12l3 3 5-6',
  tags: 'M4 4h8l8 8-8 8-8-8zM8.5 8.5h.01',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 20h4',
  share: 'M12 4v11M7 9l5-5 5 5M5 14v6h14v-6',
  shield: 'M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z',
  box: 'M4 7h16v13H4zM4 7l2-3h12l2 3M10 11h4',
} as const

const THEME_LABELS: Readonly<Record<ThemeChoice, string>> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
}

interface Counts {
  readonly tracks: number
  readonly templates: number
  readonly tags: number
  readonly reminders: number
  readonly entries: number
}

/**
 * Settings.
 *
 * The entry point for everything set up once and touched rarely: tracks,
 * check-ins, tags and reminders have no slot in the bottom navigation, so
 * this is the only route to them besides Home's shortcuts.
 *
 * The device block is not filler. This app stores `{ epochMs, timeZone }`
 * rather than a bare instant precisely because time-of-day analysis breaks
 * across DST changes and travel (ADR-0006), so what zone the device reports is
 * a real diagnostic — it is the value every timestamp gets stamped with, and
 * the first thing to check when a day's entries land on the wrong day.
 */
export function SettingsScreen() {
  const { clock, repos, storage } = useServices()
  const { status } = useStorageStatus()
  const { health } = useBackupHealth()

  const [counts, setCounts] = useState<Counts | null>(null)
  const count = useCallback(async () => {
    const [tracks, templates, tags, reminders, entries] = await Promise.all([
      repos.tracks.list(),
      repos.templates.list(),
      repos.tags.list(),
      repos.reminders.list(),
      repos.entries.count(),
    ])
    setCounts({
      tracks: tracks.length,
      templates: templates.length,
      tags: tags.length,
      reminders: reminders.length,
      entries,
    })
  }, [repos])
  useEffect(() => {
    void count()
  }, [count])

  /*
   * `null` until read, so the switch never draws in the wrong position and then
   * corrects itself. A control that visibly flips on load reads as the app
   * changing the setting rather than reporting it.
   */
  const [preferences, setPreferences] = useState<Preferences | null>(null)
  useEffect(() => {
    void (async () => {
      setPreferences(await readPreferences(storage))
    })()
  }, [storage])

  async function setFinishStep(finishStep: boolean) {
    const next = { ...(preferences ?? DEFAULT_PREFERENCES), finishStep }
    setPreferences(next)
    await writePreferences(storage, next)
  }

  const [theme, setTheme] = useState<ThemeChoice>(readTheme)

  const now = clock.now()
  const wall = wallClockOf(now)
  const time = `${String(wall.hour).padStart(2, '0')}:${String(wall.minute).padStart(2, '0')}`
  const persistence = status === null ? null : describePersistence(status.state)

  return (
    <Page>
      <h1>Settings</h1>

      {/*
        Above "Set up", because this changes what happens every time you log
        and the rows below it are things you touch once.
      */}
      <Section label="Logging">
        <Row
          title="Finish with tags and a note"
          subtitle="A last step for the context you would otherwise have to remember later"
          trailing={
            <Toggle
              label="Finish with tags and a note"
              checked={preferences?.finishStep ?? DEFAULT_PREFERENCES.finishStep}
              disabled={preferences === null}
              onChange={(next) => void setFinishStep(next)}
            />
          }
        />
      </Section>

      <Section label="Set up">
        <RowLink
          to="/tracks"
          title="Tracks"
          lead={<Tile colour="cornflower" path={ICON.tracks} />}
          detail={counts?.tracks}
        />
        <RowLink
          to="/templates"
          title="Check-ins"
          lead={<Tile colour="poppy" path={ICON.checkins} />}
          detail={counts?.templates}
        />
        <RowLink
          to="/tags"
          title="Tags"
          lead={<Tile colour="leaf" path={ICON.tags} />}
          detail={counts?.tags}
        />
        <RowLink
          to="/reminders"
          title="Reminders"
          lead={<Tile colour="heather" path={ICON.bell} />}
          detail={counts?.reminders}
        />
      </Section>

      {/*
        "Get my data out for a spreadsheet" and "make sure I do not lose my
        data" are different errands, and the hub they lead to keeps them apart:
        a CSV is not a backup (ADR-0003).
      */}
      <Section
        label="Your data"
        note={
          health === null ? undefined : (
            // The consequence, from core. Present at every level, not only
            // when things are bad: this is the screen someone opens to check,
            // and a notice that appears only on bad news says nothing by its
            // absence. Never "your data is safe".
            <span data-testid="backup-notice">
              {health.headline}. {health.detail}
            </span>
          )
        }
      >
        <RowLink
          to="/data"
          title="Export and report"
          lead={<Tile colour="marigold" path={ICON.share} />}
        />
        <RowLink
          to="/data"
          title="Backup"
          lead={<Tile colour="clay" path={ICON.box} />}
          detail={
            health === null ? undefined : (
              <span data-testid="last-backup">{lastBackupShort(health)}</span>
            )
          }
        />
        <RowLink
          to="/data"
          title="Storage"
          lead={<Tile colour="leaf" path={ICON.shield} />}
          detail={
            persistence === null ? (
              'Checking…'
            ) : (
              <span data-testid="persistence-state">{persistence.label}</span>
            )
          }
        />
      </Section>

      <section className={styles['appearance']} aria-labelledby="theme-label" data-bleed>
        <h2 id="theme-label" className={styles['label']}>
          Appearance
        </h2>
        <Segmented
          labelledBy="theme-label"
          choices={THEME_CHOICES.map((choice) => ({
            value: choice,
            label: THEME_LABELS[choice],
          }))}
          value={theme}
          onChange={(choice) => {
            setTheme(choice)
            writeTheme(choice)
          }}
        />
      </section>

      <Section label="This device">
        <Row
          title="Time zone"
          subtitle="Recorded with every entry, so times stay correct across travel"
          trailing={
            <span className={styles['mono']} data-testid="time-zone">
              {now.timeZone}
            </span>
          }
        />
        <Row
          title="Local time"
          trailing={
            <span className={styles['mono']} data-testid="local-time">
              {time}
            </span>
          }
        />
        <Row
          title="Captured as"
          trailing={
            <span className={styles['mono']} data-testid="iso">
              {toIso8601(now)}
            </span>
          }
        />
        <Row
          title="Entries stored"
          trailing={
            <span className={styles['mono']} data-testid="entry-count">
              {counts === null ? '…' : String(counts.entries)}
            </span>
          }
        />
        <Row
          title="Data schema"
          trailing={
            <span className={styles['mono']} data-testid="schema-version">
              {SCHEMA_VERSION}
            </span>
          }
        />
      </Section>

      <p className={styles['footer']}>
        Everything stays on this device. No account, no tracking.
        <br />
        anytracker <span data-testid="core-version">{CORE_VERSION}</span>
      </p>
    </Page>
  )
}
